import type { ReactElement } from 'react';

import type { Enrolment } from '@/lib/enrolmentSession';
import { ENROLMENT } from '@/lib/enrolmentSession';

import { CheckingEnrolmentScreen } from './CheckingEnrolmentScreen';
import { ConnectingScreen } from './ConnectingScreen';
import { LockedScreen } from './LockedScreen';
import { NameKioskScreen } from './NameKioskScreen';
import { PinScreen } from './PinScreen';
import { ReadyScreen } from './ReadyScreen';

/** Every enrolment state EXCEPT the one that is not a screen: `enrolled` is the candidate journey. */
export type UnenrolledState = Exclude<Enrolment, typeof ENROLMENT.enrolled>;

export interface EnrolmentFlowProps {
  readonly enrolment: UnenrolledState;
}

/**
 * Device enrolment end to end: the PIN pad, the connect result, the naming step the published branch lands
 * on, and the Ready confirmation [03-us-kad-6c, 03-us-kad-6d].
 *
 * ⭐ **The name is written by a separate, scoped `PUT /enrolment/name`** — it cannot ride on
 * `POST /enrolment`, where it does not exist yet [KAD-6c AC-2], and a second `POST` carrying one would
 * create a second enrolment row.
 *
 * The state arrives as a prop rather than being read here, so that the one state with no screen —
 * `enrolled`, which `App` answers with the candidate journey — is excluded by the TYPE instead of by a
 * branch nothing can reach. A `Record<…>` for the reason `App`'s own screen map gives: a state added to the
 * machine with nowhere to render becomes a typecheck failure here instead of a blank display in a lobby.
 */
export function EnrolmentFlow({ enrolment }: EnrolmentFlowProps) {
  return SCREENS[enrolment];
}

const SCREENS: Record<UnenrolledState, ReactElement> = {
  // ⭐ The state every load commits FIRST [03-us-kad-6i AC-11]. It is a real entry here rather than an early
  // `return null` upstream, so the exhaustive map keeps refusing a state with nowhere to render [`0307`].
  [ENROLMENT.checking]: <CheckingEnrolmentScreen />,
  [ENROLMENT.pin]: <PinScreen />,
  [ENROLMENT.connecting]: <ConnectingScreen />,
  [ENROLMENT.locked]: <LockedScreen />,
  [ENROLMENT.naming]: <NameKioskScreen />,
  [ENROLMENT.ready]: <ReadyScreen />,
};
